import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NavigationLink } from '@domgen/dgx-fe-common';
import { AccordionPanel } from '@domgen/dgx-fe-components/accordion';

import { TranslateService } from '@ngx-translate/core';
import { PlanCategoryType } from '../../constants/plan-category-type.enum';
import { upperCaseBrands } from '../../manage-my-repair/brand-format-list';
import { MMRClaimEvent } from './manage-my-repair.container';
import {
  ManageMyRepairResponse,
  RepairHistoryResponseResult,
} from '../../types/manage-my-repair-types';
import { CONFIG } from '@domgen/dgx-fe-config';
import { AppConfig } from '../../types/app-config.model';
import { ServiceActionEnum } from '../../types/plan-detail.types';
import { SmartFixBanner } from '../../manage-my-repair/components/smart-fix-banner/smart-fix-banner';
import { ManageMyRepairService } from '../../services/manage-my-repair.service';
import { AppliancesService } from '../../services/appliances.service';
import { AnalyticsService, FeatureFlagService } from '@domgen/dgx-components';
import { User } from '../../types/user.model';
import {
  BannerConfig,
  BannerConfigItem,
  MaintenanceBannerParams,
  MaintenanceBannerPayload,
} from '../../types/maintenance-banner.model';

@Component({
  selector: 'claims-athome-myaccount-manage-my-repair',
  templateUrl: './manage-my-repair.component.html',
  styleUrls: ['./manage-my-repair.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageMyRepairComponent implements OnChanges, OnInit {
  @Input() claims: ManageMyRepairResponse;

  @Input() appointmentsHistory: RepairHistoryResponseResult[];

  @Input() isHistoryShow?: boolean;

  @Input() lastUpdated: Date | undefined;

  @Input() smartFixBanner: SmartFixBanner;

  @Input() isNewOrbitFlow: boolean;

  @Output() viewPlanDetails: EventEmitter<void> = new EventEmitter<void>();

  @Output() backButtonClicked: EventEmitter<void> = new EventEmitter<void>();

  @Output() rebookClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() cancelClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() createClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() trackClaim: EventEmitter<string> = new EventEmitter<string>();

  allFaqsLink: NavigationLink = {
    href: 'https://www.domesticandgeneral.com/content/help-advice-section/faq',
    label: this.translateService.instant('MMR.FAQS.LINK'),
  };

  heating_faqs: AccordionPanel[];
  whitegoods_faqs: AccordionPanel[];
  upperCaseBrands: string[] = upperCaseBrands;
  repairerName: string;
  isRPC = false;
  subtitle: string;
  user: User;

  isSmartFixHubEnabled: boolean;
  maintenanceBannerPayload: MaintenanceBannerPayload | null = null;

  address = this.appliancesService.getPersonCorresponsenceAddress(
    this.mmrService.userData['custom:digital_id']
  );

  get supportNeedsLink() {
    return this.config.myPreferencesPage;
  }
  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,
    private readonly translateService: TranslateService,
    private readonly appliancesService: AppliancesService,
    private readonly mmrService: ManageMyRepairService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.isSmartFixHubBannerEnabled();
    this.maintenanceBannerPayload =
      this.featureFlagService.getFeatureFlagPayload(
        'maintenance-banner'
      ) as MaintenanceBannerPayload | null;
    this.user = this.mmrService.userData;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.claims) {
      this.isRPC = this.isArgosRPC();
      if (!this.claims) return;

      this.subtitle = this.getSubtitle();
      this.repairerName = this.repairerNameFAQ;
      this.heating_faqs = this.getFaqs(
        'MMR.FAQS.HEATING',
        2,
        this.repairerName
      );
      this.whitegoods_faqs = this.getFaqs(
        'MMR.FAQS.WHITEGOODS',
        5,
        this.repairerName
      );
    }
  }

  get showServiceDetails(): boolean {
    if (!this.claims) return false;
    let { serviceAction } = this.claims;
    const { openService } = this.claims;

    const hasAsvClaimType =
      this.claims.availableClaimTypes?.some(
        (claimType) => claimType?.claimTypeCode?.toUpperCase() === 'ASV'
      ) ?? false;

    // Only go to unknown if we haven't got a service option and no open service, claim types become empty when an active claim in progress
    if (!hasAsvClaimType && !openService) {
      serviceAction = ServiceActionEnum.UNKNOWN;
    }

    const showServiceOptionStatus: string[] = [
      ServiceActionEnum.REQUEST_SERVICE,
      ServiceActionEnum.AMEND_SERVICE,
      ServiceActionEnum.INCOMPLETE_FORM,
      ServiceActionEnum.BOOK_ENGINEER,
      ServiceActionEnum.IN_WAIT,
      ServiceActionEnum.IN_WARRANTY,
    ];

    if (this.claims.openService) {
      showServiceOptionStatus.push(ServiceActionEnum.NO_ACTION);
    }

    if (
      serviceAction === ServiceActionEnum.NO_ACTION &&
      this.claims.openService
    ) {
      return true;
    }

    return showServiceOptionStatus.includes(serviceAction);
  }

  get faqs(): AccordionPanel[] {
    const planCategoryCode = this.claims?.planCategoryCode;
    if (!planCategoryCode) return [];

    switch (planCategoryCode) {
      case PlanCategoryType.Whitegoods:
        return this.whitegoods_faqs;
      case PlanCategoryType.Heating:
        return this.heating_faqs;
      default:
        return [];
    }
  }

  private getSubtitle(): string {
    if (!this.claims || this.isArgosRPC()) return '';

    // if claim type is self fit
    if (this.claims.openRepair?.claimTypeId === '28')
      return 'MMR.SUBTITLE.WHP_OEM';

    return this.claims.bookingOEM === 'whirlpool' ||
      this.claims.bookingOEM.toLocaleLowerCase() === 'beko'
      ? 'MMR.SUBTITLE.WHP_OEM'
      : 'MMR.SUBTITLE.NON_WHP_OEM';
  }

  private getFaqs(
    category: string,
    questionCount: number,
    repairerName: string
  ): AccordionPanel[] {
    return Array.from({ length: questionCount }, (_, i) => ({
      title: this.translateService.instant(`${category}.QUESTION_${i + 1}`),
      body: this.translateService.instant(`${category}.ANSWER_${i + 1}`, {
        repairerName,
      }),
    }));
  }

  get repairerNameFAQ() {
    if (!this.claims.openRepair && !this.claims.openService) {
      const manufacturer = this.claims.item.manufacturer;
      return (
        manufacturer.charAt(0).toUpperCase() +
        manufacturer.slice(1).toLowerCase()
      );
    }

    if (this.claims.openRepair && this.claims.openService) {
      return 'your service provider';
    }

    return (
      (this.claims.openRepair || this.claims.openService)?.repairer?.name
        ?.split(' ')
        .map((brandName) => {
          if (brandName.length === 2) {
            return brandName.toUpperCase();
          } else {
            return (
              brandName.charAt(0).toUpperCase() +
              brandName.slice(1).toLowerCase()
            );
          }
        })
        .join(' ') ?? ''
    );
  }

  isSmartFixHubBannerEnabled(): void {
    this.isSmartFixHubEnabled = this.featureFlagService.isFeatureFlagEnabled(
      'smart-fix-hub-banner'
    );
  }

  get isMaintenanceBanner() {
    return (
      Boolean(this.maintenanceBannerPayload?.length) ||
      this.config.runtimeFeatures.maintenanceBanner.display
    );
  }

  convertToTitleCase(str: any) {
    return str
      .toLowerCase()
      .split(' ')
      .map((word: any) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  maintenanceBannerParams(): MaintenanceBannerParams | null {
    const bannerConfig = this.getMaintenanceBannerConfig();
    if (!bannerConfig) {
      return this.config.runtimeFeatures.maintenanceBanner?.allBrands
        ? {}
        : null;
    }

    const brand = this.claims?.item?.manufacturer?.trim().toLowerCase();
    for (const banner of bannerConfig) {
      if (!this.isBannerWithinDateRange(banner)) continue;

      const content = {
        date: banner.date,
        title: banner.title,
        message: banner.message,
        contactCentreMessage: banner.contactCentreMessage,
        apologyMessage: banner.apologyMessage,
      };

      if (banner.allBrands) {
        return content;
      }
      if (brand && this.matchesBrand(banner, brand)) {
        return { brand: this.convertToTitleCase(brand), ...content };
      }
    }
    return null;
  }

  private matchesBrand(banner: BannerConfigItem, brand: string): boolean {
    return banner.brands.some((configuredBrand) => {
      const normalised = configuredBrand.trim().toLowerCase();
      return normalised !== '' && brand.startsWith(normalised);
    });
  }

  private isBannerWithinDateRange(
    banner: Pick<BannerConfigItem, 'startDate' | 'endDate'>
  ): boolean {
    const today = new Date();
    const startDate = banner.startDate
      ? new Date(`${banner.startDate}T00:00:00`)
      : null;
    const endDate = banner.endDate
      ? new Date(`${banner.endDate}T23:59:59.999`)
      : null;

    return (!startDate || today >= startDate) && (!endDate || today <= endDate);
  }

  private getMaintenanceBannerConfig(): BannerConfig | undefined {
    return this.maintenanceBannerPayload?.length
      ? this.maintenanceBannerPayload
      : this.config.runtimeFeatures.maintenanceBanner?.banner_config;
  }

  viewDetails() {
    this.viewPlanDetails.emit();
  }

  isRpcPresent(): boolean {
    const codeDescription = this.claims.item?.code_description;
    return codeDescription?.toLocaleLowerCase().includes('rpc');
  }

  private isArgosRPC(): boolean {
    const manufacturer = this.claims?.item?.manufacturer?.toLowerCase();
    const codeDescription = this.claims?.item?.code_description?.toLowerCase();

    this.isRPC = manufacturer === 'argos' && codeDescription?.includes('rpc');
    return this.isRPC;
  }

  gotoFAQs() {
    window.open(this.allFaqsLink.href, '_blank');
  }

  onAccordionToggled(data: {
    body: string;
    isExpanded: boolean;
    title: string;
  }) {
    this.analyticsService.push({
      event: 'genericGAEvent',
      pagename: 'claims-what-happened',
      category: 'claims-2.0',
      clientName: 'DG Claims',
      eventCategory: 'claims what happened',
      eventAction: 'see more help and advice',
      eventLabel: data?.title || '',
      DigitalID: this.user?.['custom:digital_id'] || '',
      customerID: this.user?.sub || '',
      planNumber: this.claims?.planNumber || '',
      productName: `${this.claims?.item?.manufacturer || ''} ${
        this.claims?.item?.code_description || ''
      }`.toLowerCase(),
      productModel: this.claims?.item?.model_number || '',
      repairer: this.claims?.openRepair?.repairer || '',
      repairTimeline: this.claims?.openRepair?.isClaimsTrackerEnabled || '',
      claimStatus: this.claims?.openRepair?.claimStatus || '',
    });
  }

  trackClick(linkTitle: string) {
    this.analyticsService.push({
      event: 'genericGAEvent',
      pagename: 'claims-what-happened',
      category: 'claims-2.0',
      clientName: 'DG Claims',
      eventCategory: 'claims what happened',
      eventAction: 'click links',
      eventLabel: linkTitle,
      DigitalID: this.user?.['custom:digital_id'] || '',
      customerID: this.user?.sub || '',
      planNumber: this.claims?.planNumber || '',
      productName: `${this.claims?.item?.manufacturer || ''} ${
        this.claims?.item?.code_description || ''
      }`.toLowerCase(),
      productModel: this.claims?.item?.model_number || '',
      repairer: this.claims?.openRepair?.repairer || '',
      repairTimeline: this.claims?.openRepair?.isClaimsTrackerEnabled || '',
      claimStatus: this.claims?.openRepair?.claimStatus || '',
    });
  }
}
